import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Bell, Smartphone, Mail, Info } from 'lucide-react';

const CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: '#25D366' },
  { id: 'push', label: 'Push', icon: Bell, color: '#6366F1' },
  { id: 'sms', label: 'SMS', icon: Smartphone, color: '#F59E0B' },
];

const COSTS = {
  whatsapp: 0.0581,
  sms: 0.0395,
  push: 0.0,
  email: 0.005,
};

export function ChannelSelector({
  selected = ['whatsapp'],
  onChange,
  recipientCount = 0,
  pushSubscribers = 0,
  whatsappLocked = false,
  whatsappLockReason = '',
}) {
  const toggle = (id) => {
    const ch = CHANNELS.find(c => c.id === id);
    if (!ch || ch.disabled) return;
    if (id === 'whatsapp' && whatsappLocked) return;
    const next = selected.includes(id) ? selected.filter(c => c !== id) : [...selected, id];
    if (next.length === 0) return; // at least one channel must stay selected
    onChange(next);
  };

  const totalCost = selected.reduce((sum, ch) => {
    const count = ch === 'push' ? pushSubscribers : recipientCount;
    return sum + (COSTS[ch] || 0) * count;
  }, 0);

  return (
    <div data-testid="channel-selector">
      <p className="text-xs font-semibold text-[#1A1A1A] mb-2">Canali di invio</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {CHANNELS.map(ch => {
          const Icon = ch.icon;
          const active = selected.includes(ch.id);
          const cost = COSTS[ch.id] || 0;
          const count = ch.id === 'push' ? pushSubscribers : recipientCount;
          const locked = ch.id === 'whatsapp' && whatsappLocked;
          const disabled = ch.disabled || locked;
          return (
            <button key={ch.id} onClick={() => toggle(ch.id)} data-testid={`channel-${ch.id}`}
              disabled={disabled}
              title={locked ? whatsappLockReason : ''}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-xs font-medium transition-all ${
                disabled ? 'opacity-40 cursor-not-allowed border-slate-100 bg-slate-50 text-[#aaa]' :
                active ? 'shadow-sm' : 'border-slate-100 bg-slate-50 text-[#64748B] hover:border-slate-200'
              }`}
              style={active && !disabled ? { borderColor: `${ch.color}60`, backgroundColor: `${ch.color}08`, color: ch.color } : {}}>
              <Icon size={14} />
              <span>{ch.label}</span>
              {!disabled && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ml-0.5 ${
                  cost === 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-[#64748B]'
                }`}>
                  {cost === 0 ? 'Gratis' : `€${(cost * count).toFixed(2)}`}
                </span>
              )}
              {locked && <span className="text-[10px] italic ml-0.5">Non disponibile</span>}
            </button>
          );
        })}
      </div>

      {/* Email moved out of broadcast: dedicated builder for HTML/CTA/images */}
      <Link to="/admin/email-templates" data-testid="email-marketing-link"
        className="inline-flex items-center gap-1.5 text-[11px] text-[#3B82F6] hover:text-[#1D4ED8] underline-offset-2 hover:underline mb-2">
        <Mail size={12} /> Per inviare email con immagini/CTA → vai a Email Marketing
      </Link>

      {whatsappLocked && (
        <div className="mt-1 mb-1 flex items-start gap-1.5 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="whatsapp-locked-notice">
          <Info size={11} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-800 leading-relaxed">{whatsappLockReason || 'WhatsApp richiede un template approvato da Meta.'}</p>
        </div>
      )}

      {selected.length > 0 && recipientCount > 0 && (
        <p className="text-[10px] text-[#64748B]" data-testid="channel-cost-summary">
          Costo stimato totale: <span className="font-bold text-[#1A1A1A]">€{totalCost.toFixed(2)}</span>
          {selected.includes('push') && <span className="ml-1">({pushSubscribers} iscritti push)</span>}
        </p>
      )}
    </div>
  );
}

export function ChannelBadges({ channels = ['whatsapp'] }) {
  const ALL = [...CHANNELS, { id: 'email', label: 'Email', icon: Mail, color: '#3B82F6' }];
  return (
    <div className="flex gap-1 flex-wrap">
      {ALL.filter(ch => channels.includes(ch.id)).map(ch => {
        const Icon = ch.icon;
        return (
          <span key={ch.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium"
            style={{ backgroundColor: `${ch.color}12`, color: ch.color }}>
            <Icon size={10} /> {ch.label}
          </span>
        );
      })}
    </div>
  );
}

export { CHANNELS, COSTS };
